// The equipment register: what's on it, what it's worth against the tools
// limit, and add / edit / confirm / mark sold / remove. Stolen is set by a
// claim only; removing is a soft delete the engine keeps for audit.
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Camera, Check, Plus, Search, Trash2, Wrench } from 'lucide-react';
import { ApiError, ok } from '@cover/core/api/client.ts';
import { Thumb } from '@cover/core/ui/Thumb.tsx';
import type { Asset, AssetCategory, AssetSummary } from '@cover/core/api/types.ts';
import { useBrand, whatsappLink } from '@cover/core/brand.tsx';
import { CATEGORY_LABEL, VALUE_BASIS_LABEL, date, money } from '@cover/core/format.ts';
import { Badge } from '@cover/core/ui/Badge.tsx';
import { Button } from '@cover/core/ui/Button.tsx';
import { Card, CardBody, CardHeader } from '@cover/core/ui/Card.tsx';
import { Dialog } from '@cover/core/ui/Dialog.tsx';
import { Field, Input, MoneyInput, Select } from '@cover/core/ui/Form.tsx';
import { Empty, Load } from '@cover/core/ui/States.tsx';
import { useToast } from '@cover/core/ui/Toast.tsx';
import { api, keys, useAssetSummary, useAssets, useClaims, useDocuments } from '../api.ts';
import { UploadButton } from '../components/Upload.tsx';
import { PageTitle } from '../Layout.tsx';
import { useBusinessId } from '../session.tsx';

const CATEGORIES = Object.entries(CATEGORY_LABEL) as [AssetCategory, string][];

function useRefreshAssets() {
  const qc = useQueryClient();
  const b = useBusinessId();
  return () => Promise.all([qc.invalidateQueries({ queryKey: keys.assets(b) }), qc.invalidateQueries({ queryKey: keys.summary(b) }), qc.invalidateQueries({ queryKey: keys.profile(b) })]);
}

export function EquipmentPage() {
  const assets = useAssets();
  const summary = useAssetSummary();
  const [editing, setEditing] = useState<Asset | 'new' | null>(null);
  const [filter, setFilter] = useState<AssetCategory | 'all'>('all');
  const [q, setQ] = useState('');

  return (
    <>
      <PageTitle lead="Keep a list of your tools and kit. If anything’s stolen, you just pick it from here.">Equipment</PageTitle>
      <div className="space-y-5">
        <Load q={summary}>{(s) => <SummaryCard s={s} />}</Load>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <Button onClick={() => setEditing('new')}>
            <Plus className="h-4 w-4" aria-hidden /> Add an item
          </Button>
          <div className="flex flex-1 flex-wrap items-center justify-end gap-2">
          <label className="relative min-w-40 flex-1 sm:max-w-56">
            <span className="sr-only">Search your equipment</span>
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" aria-hidden />
            <Input type="search" className="py-2 pl-9" placeholder="Search" value={q} onChange={(e) => setQ(e.target.value)} />
          </label>
          <label className="flex items-center gap-2 text-sm">
            <span className="text-mid">Show</span>
            <Select className="w-auto py-2" value={filter} onChange={(e) => setFilter(e.target.value as AssetCategory | 'all')}>
              <option value="all">Everything</option>
              {CATEGORIES.map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </Select>
          </label>
          </div>
        </div>

        <Load q={assets}>
          {(all) => {
            const term = q.trim().toLowerCase();
            const list = all.filter((a) => (filter === 'all' || a.category === filter) && (!term || [a.name, a.make, a.model, a.serial].some((v) => v?.toLowerCase().includes(term))));
            const toCheck = list.filter((a) => a.status === 'active' && !a.confirmed);
            const active = list.filter((a) => a.status === 'active' && a.confirmed);
            const stolen = list.filter((a) => a.status === 'stolen');
            const sold = list.filter((a) => a.status === 'sold');
            if (!list.length)
              return (
                <Card>
                  <Empty icon={<Wrench className="h-8 w-8" aria-hidden />} title={!all.length ? 'No equipment yet' : term ? `Nothing matches “${q.trim()}”` : 'Nothing in this category'}>
                    Add things here, send a photo of a tool or receipt in WhatsApp, or forward receipt emails to your address on the Connections page.
                  </Empty>
                </Card>
              );
            return (
              <>
                {toCheck.length > 0 && <AssetSection title="Please check" description="We picked these up from your messages, photos and emails. Tap “Right” if they’re correct." assets={toCheck} onEdit={setEditing} />}
                {active.length > 0 && <AssetSection title="Your kit" assets={active} onEdit={setEditing} />}
                {stolen.length > 0 && <AssetSection title="Stolen" assets={stolen} onEdit={setEditing} />}
                {sold.length > 0 && <AssetSection title="Sold" assets={sold} onEdit={setEditing} />}
              </>
            );
          }}
        </Load>
      </div>
      {editing && <AssetDialog asset={editing === 'new' ? undefined : editing} onClose={() => setEditing(null)} />}
    </>
  );
}

function SummaryCard({ s }: { s: AssetSummary }) {
  const brand = useBrand();
  const limit = s.coverLimitPence;
  const pct = limit ? Math.min(100, Math.round((s.totalPence / limit) * 100)) : 0;
  const over = limit != null && s.totalPence > limit;
  const flag = s.flags.find((f) => f.code === 'under_insured' || f.code === 'no_tools_cover');
  const wa = whatsappLink(brand, `My tools come to ${money(s.totalPence)}. Can I raise my tools cover?`);
  return (
    <Card>
      <CardBody className="space-y-3">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <p className="text-sm text-mid">Your equipment is worth</p>
          <p className="text-2xl font-bold">{money(s.totalPence)}</p>
        </div>
        {limit != null && (
          <>
            <div className="h-3 overflow-hidden rounded-full bg-canvas" role="img" aria-label={`${money(s.totalPence)} of a ${money(limit)} tools limit`}>
              <div className={over ? 'h-full bg-warn-fg' : 'h-full bg-brand'} style={{ width: `${over ? 100 : pct}%` }} />
            </div>
            <p className="text-sm">
              Tools cover: <strong>{money(limit)}</strong>
            </p>
          </>
        )}
        {flag && (
          <div className="rounded-md bg-warn-bg p-3 text-warn-fg">
            <p className="font-semibold">{flag.message}</p>
            <p className="text-sm">
              {flag.shortfallPence ? `About ${money(flag.shortfallPence)} of your kit isn’t covered. ` : ''}
              {wa && (
                <a href={wa} className="font-semibold text-warn-fg underline">
                  Talk to us about it
                </a>
              )}
            </p>
          </div>
        )}
        {!!s.unconfirmedPence && (
          <p className="text-sm text-mid">{money(s.unconfirmedPence)} of this is estimates or not yet checked. A receipt or photo makes a claim simpler.</p>
        )}
      </CardBody>
    </Card>
  );
}

function NoPhoto() {
  return (
    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-canvas" aria-hidden>
      <Wrench className="h-5 w-5 text-muted" />
    </div>
  );
}

function AssetSection({ title, description, assets, onEdit }: { title: string; description?: string; assets: Asset[]; onEdit: (a: Asset) => void }) {
  const claims = useClaims();
  const docs = useDocuments();
  const toast = useToast();
  const refresh = useRefreshAssets();
  const confirm = useMutation({
    mutationFn: (ids: string[]) => Promise.all(ids.map((id) => ok(api.PATCH('/assets/{assetId}', { params: { path: { assetId: id } }, body: { confirm: true } })))),
    onSuccess: async (_r, ids) => {
      await refresh();
      toast.ok(ids.length > 1 ? 'Thanks, all confirmed.' : 'Thanks, confirmed.');
    },
    onError: (e) => toast.fail(e),
  });
  const unconfirmed = assets.filter((a) => !a.confirmed && a.status === 'active');
  // Only photos make thumbnails; receipts and PDFs don't.
  const photoOf = (a: Asset) => a.documentIds?.find((id) => docs.data?.find((d) => d.id === id)?.contentType.startsWith('image/'));
  return (
    <Card>
      <CardHeader
        title={`${title} (${assets.length})`}
        description={description}
        action={
          unconfirmed.length > 1 ? (
            <Button size="sm" variant="secondary" busy={confirm.isPending && confirm.variables?.length === unconfirmed.length} onClick={() => confirm.mutate(unconfirmed.map((a) => a.id))}>
              All right
            </Button>
          ) : undefined
        }
      />
      <CardBody className="pt-1">
        <ul className="divide-y divide-line">
          {assets.map((a) => {
            const claim = a.claimId ? claims.data?.find((c) => c.id === a.claimId) : undefined;
            return (
              <li key={a.id} className="flex gap-3 py-3">
                {photoOf(a) ? <Thumb documentId={photoOf(a)!} alt="" className="h-12 w-12 shrink-0 rounded-md object-cover" fallback={<NoPhoto />} /> : <NoPhoto />}
                <div className="min-w-0 flex-1">
                  <p className="font-semibold">{a.name}</p>
                  <p className="text-sm text-mid">
                    {[a.category && CATEGORY_LABEL[a.category], a.serial && `Serial ${a.serial}`].filter(Boolean).join(' · ')}
                  </p>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {a.valueBasis && <Badge>{VALUE_BASIS_LABEL[a.valueBasis]}</Badge>}
                    {a.status === 'stolen' && (claim ? <Link to={`/claims/${claim.id}`} className="text-sm">On claim {claim.reference}</Link> : <Badge tone="bad">Stolen</Badge>)}
                  </div>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1">
                  <span className="font-semibold">{money(a.valuePence)}</span>
                  {a.status !== 'stolen' && (
                    <div className="flex gap-1">
                      {!a.confirmed && a.status === 'active' && (
                        <Button variant="secondary" size="sm" className="min-h-11" busy={confirm.isPending && confirm.variables?.length === 1 && confirm.variables[0] === a.id} onClick={() => confirm.mutate([a.id])} aria-label={`${a.name} is right`}>
                          <Check className="h-4 w-4" aria-hidden /> Right
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" className="min-h-11" onClick={() => onEdit(a)} aria-label={`${a.confirmed ? 'Edit' : 'Change'} ${a.name}`}>
                        {a.confirmed ? 'Edit' : 'Change'}
                      </Button>
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </CardBody>
    </Card>
  );
}

type Draft = { name: string; make: string; model: string; serial: string; category: AssetCategory | ''; valuePence: number | null; purchaseDate: string; documentIds: string[] };

function AssetDialog({ asset, onClose }: { asset?: Asset; onClose: () => void }) {
  const b = useBusinessId();
  const toast = useToast();
  const refresh = useRefreshAssets();
  const [d, setD] = useState<Draft>({
    name: asset?.name ?? '',
    make: asset?.make ?? '',
    model: asset?.model ?? '',
    serial: asset?.serial ?? '',
    category: asset?.category ?? '',
    valuePence: asset?.valuePence ?? null,
    purchaseDate: asset?.purchaseDate ?? '',
    documentIds: asset?.documentIds ?? [],
  });
  const [errors, setErrors] = useState<Partial<Record<keyof Draft, string>>>({});
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setD((x) => ({ ...x, [k]: v }));
  const valueChanged = asset && d.valuePence !== asset.valuePence;

  const save = useMutation({
    mutationFn: async (extra: { status?: 'sold' } = {}) => {
      const fields = {
        name: d.name.trim(),
        make: d.make.trim() || undefined,
        model: d.model.trim() || undefined,
        serial: d.serial.trim() || undefined,
        category: d.category || undefined,
        valuePence: d.valuePence ?? undefined,
        purchaseDate: d.purchaseDate || undefined,
      };
      if (asset) {
        return ok(
          api.PATCH('/assets/{assetId}', {
            params: { path: { assetId: asset.id } },
            // A value the customer types is their figure, not a receipt or estimate.
            body: { ...fields, ...(valueChanged ? { valueBasis: 'customer' as const } : {}), ...extra, confirm: true },
          }),
        );
      }
      // The customer typed it in, so it's theirs: confirmed on create.
      return ok(
        api.POST('/businesses/{businessId}/assets', {
          params: { path: { businessId: b } },
          body: { ...fields, source: 'dashboard', confidence: 1, valueBasis: d.valuePence != null ? 'customer' : undefined, documentIds: d.documentIds, confirm: true },
        }),
      );
    },
    onSuccess: async (_a, extra) => {
      await refresh();
      toast.ok(extra?.status === 'sold' ? 'Marked as sold.' : asset ? 'Saved.' : 'Added to your equipment.');
      onClose();
    },
    onError: (e) => toast.fail(e),
  });

  const [removing, setRemoving] = useState(false);
  const remove = useMutation({
    mutationFn: () => ok(api.DELETE('/assets/{assetId}', { params: { path: { assetId: asset!.id } } })),
    onSuccess: async () => {
      await refresh();
      toast.ok(`${asset!.name} removed.`);
      onClose();
    },
    onError: (e) =>
      toast.fail(e instanceof ApiError && e.status === 409 ? new Error('This item is part of a claim, so it stays on your list.') : e),
  });

  function submit() {
    const errs: typeof errors = {};
    if (!d.name.trim()) errs.name = 'What is it? For example “Makita combi drill”.';
    if (d.valuePence == null) errs.valuePence = 'Roughly what would it cost to replace?';
    setErrors(errs);
    if (!Object.keys(errs).length) save.mutate({});
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title={asset ? (asset.confirmed ? `Edit ${asset.name}` : 'Is this right?') : 'Add an item'} description={asset && !asset.confirmed ? 'We picked this up from a message, photo or email. Fix anything that’s wrong, then save.' : undefined}>
      <form
        className="space-y-4"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <Field label="What is it?" error={errors.name}>
          <Input value={d.name} onChange={(e) => set('name', e.target.value)} placeholder="Makita combi drill" />
        </Field>
        <Field label="Type">
          <Select value={d.category} onChange={(e) => set('category', e.target.value as AssetCategory)}>
            <option value="">Choose…</option>
            {CATEGORIES.map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </Select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Make">
            <Input value={d.make} onChange={(e) => set('make', e.target.value)} />
          </Field>
          <Field label="Model">
            <Input value={d.model} onChange={(e) => set('model', e.target.value)} />
          </Field>
        </div>
        <Field label="Serial number" hint="Usually on a sticker or plate. Helps the police if it’s stolen.">
          <Input value={d.serial} onChange={(e) => set('serial', e.target.value)} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Value to replace" error={errors.valuePence}>
            <MoneyInput pence={d.valuePence} onPence={(p) => set('valuePence', p)} />
          </Field>
          <Field label="Bought on">
            <Input type="date" value={d.purchaseDate} onChange={(e) => set('purchaseDate', e.target.value)} />
          </Field>
        </div>
        {asset?.valueBasis && <p className="text-sm text-mid">Value based on: {VALUE_BASIS_LABEL[asset.valueBasis].toLowerCase()}.</p>}
        {!asset && (
          <div className="space-y-2">
            <p className="text-sm font-semibold">Photo or receipt (optional)</p>
            <UploadButton size="sm" label={d.documentIds.length ? 'Add another' : 'Add a photo or receipt'} accept="image/*,application/pdf" onUploaded={(doc) => set('documentIds', [...d.documentIds, doc.id])} />
            {d.documentIds.length > 0 && (
              <p className="flex items-center gap-1 text-sm text-ok-fg">
                <Camera className="h-4 w-4" aria-hidden /> {d.documentIds.length} attached
              </p>
            )}
          </div>
        )}
        {asset?.purchaseDate && asset.supplier && <p className="text-sm text-mid">Bought from {asset.supplier} on {date(asset.purchaseDate)}.</p>}
        <div className="flex flex-wrap gap-2 pt-1">
          <Button type="submit" busy={save.isPending && !save.variables?.status}>
            {asset && !asset.confirmed ? 'That’s right, save' : 'Save'}
          </Button>
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          {asset?.status === 'active' && (
            <Button type="button" variant="ghost" className="ml-auto" busy={save.isPending && save.variables?.status === 'sold'} onClick={() => save.mutate({ status: 'sold' })}>
              I’ve sold it
            </Button>
          )}
        </div>
        {asset && asset.status !== 'stolen' && (
          <div className="border-t border-line pt-3">
            {removing ? (
              <div className="space-y-2 rounded-md bg-bad-bg p-3" role="alert">
                <p className="text-sm text-bad-fg">Remove {asset.name} from your list? Only do this if it was added by mistake.</p>
                <div className="flex gap-2">
                  <Button type="button" variant="danger" size="sm" className="min-h-11" busy={remove.isPending} onClick={() => remove.mutate()}>
                    Yes, remove it
                  </Button>
                  <Button type="button" variant="secondary" size="sm" className="min-h-11" onClick={() => setRemoving(false)}>
                    Keep it
                  </Button>
                </div>
              </div>
            ) : (
              <Button type="button" variant="ghost" size="sm" className="min-h-11 text-bad-fg hover:bg-bad-bg" onClick={() => setRemoving(true)}>
                <Trash2 className="h-4 w-4" aria-hidden /> Remove from my list
              </Button>
            )}
          </div>
        )}
      </form>
    </Dialog>
  );
}
